"use client";
import { useMemo } from "react";
import { useTranslations } from "@zapier/i18n";
import {
  ManagedConnectionSource,
  Table,
  type BulkSelectAction,
} from "@zapier/shared";
import { Connection } from "@zapier/client";
import {
  SortDescriptor,
  SortDirection,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";

import ConnectionRow from "./ConnectionRow";
import { EmptyConnectionsTable } from "./EmptyConnectionsTable";
import { ConnectionModals } from "../ConnectionModals";
import {
  useShowEmptyConnectionsTable,
  EmptyConnectionsTable as EmptyConnectionsTableEnum,
} from "./useShowEmptyConnectionsTable";
import { ConnectionErrorCallbackPayload, ConnectionModal } from "../types";

import styles from "./ConnectionsTable.module.css";

const TITLE_COLUMN_WIDTH = 350;
const APP_COLUMN_WIDTH = 200;
const STATUS_COLUMN_WIDTH = 250;
const COUNT_COLUMN_WIDTH = 80;
const LAST_MODIFIED_COLUMN_WIDTH = 170;
const ACCESS_COLUMN_WIDTH = 160;
const TABLE_MIN_WIDTH = 930;

const fixedWidth = (width: number) => ({
  defaultWidth: width,
  minWidth: width,
  maxWidth: width,
});

const CountColumnLabel = ({
  label,
  tooltip,
}: {
  label: string;
  tooltip: string;
}) => {
  return (
    <span className={styles.countColumnLabel}>
      {label}
      <Tooltip allowMultiline={true} content={tooltip}>
        <TooltipFocusableTrigger>
          <Icon name="alertInfo" size="1em" color="TextWeakest" />
        </TooltipFocusableTrigger>
      </Tooltip>
    </span>
  );
};

interface Props {
  allowsSorting?: boolean;
  ariaLabel: string;
  bulkSelectActions?: BulkSelectAction[];
  connections: Connection[];
  currentConnection: Connection | undefined;
  currentConnectionModal: ConnectionModal | undefined;
  // Precomputed by the caller (e.g. Connections.tsx); takes precedence over
  // this component's own computation so the two can't drift.
  emptyTableType?: EmptyConnectionsTableEnum;
  isAppNonCompliant?: (appId: string) => boolean;
  isNonCompliantFilter?: boolean;
  isLoading?: boolean;
  isLoadingSecondary?: boolean; // loading secondary data (implementations, members)
  // Attributes AddConnectionModal's created/abandoned events to a surface.
  managedConnectionSource?: ManagedConnectionSource;
  onAddConnection: () => void;
  onAddManagedConnection: () => void;
  onConnectionError?: (error: ConnectionErrorCallbackPayload) => void;
  onSortChange?: (sortDescriptor: SortDescriptor) => void;
  selectableEntities?: { id: string | number }[];
  setCurrentConnection: (connection: Connection | undefined) => void;
  setCurrentConnectionModal: (modal?: ConnectionModal | undefined) => void;
  showCheckboxes?: boolean;
  connectionIconOverride?: string; // override icon for all connections
  withModals?: boolean; // whether to render the connection modals
  // Total connections across all pages, used as a fallback when
  // emptyTableType isn't precomputed by the caller.
  totalCount?: number;
}

const ConnectionsTable = ({
  allowsSorting = true,
  ariaLabel,
  bulkSelectActions,
  connections,
  currentConnection,
  currentConnectionModal,
  emptyTableType: emptyTableTypeProp,
  isAppNonCompliant,
  isNonCompliantFilter,
  isLoading,
  isLoadingSecondary,
  managedConnectionSource,
  onAddConnection,
  onAddManagedConnection,
  onConnectionError,
  onSortChange,
  selectableEntities,
  setCurrentConnection,
  setCurrentConnectionModal,
  showCheckboxes = false,
  connectionIconOverride,
  totalCount,
  withModals = true,
}: Props) => {
  const t = useTranslations("connections-table");
  const tAssetTypes = useTranslations("asset-types");
  const columns = useMemo(() => {
    return [
      {
        label: t("columns.app"),
        allowsSorting,
        className: styles.appColumn,
        sortKey: "selected_api",
        ...fixedWidth(APP_COLUMN_WIDTH),
      },
      {
        allowsSorting,
        label: t("columns.status"),
        sortKey: "status",
        ...fixedWidth(STATUS_COLUMN_WIDTH),
      },
      {
        label: (
          <CountColumnLabel
            label={t("columns.zaps")}
            tooltip={t("tooltips.zaps-count")}
          />
        ),
        allowsSorting,
        sortKey: "zap_count",
        ...fixedWidth(COUNT_COLUMN_WIDTH),
      },
      {
        label: (
          <CountColumnLabel
            label={t("columns.last-modified")}
            tooltip={t("tooltips.last-modified")}
          />
        ),
        allowsSorting,
        sortKey: "updated_at",
        ...fixedWidth(LAST_MODIFIED_COLUMN_WIDTH),
      },
      {
        label: t("columns.people-with-access"),
        ...fixedWidth(ACCESS_COLUMN_WIDTH),
      },
    ];
  }, [t, allowsSorting]);

  const sortDescriptor = {
    column: "updated_at",
    direction: "descending" as SortDirection,
  };

  const computedEmptyTableType = useShowEmptyConnectionsTable({
    isLoading,
    hasNoConnections: connections.length === 0,
    sortDescriptor,
    totalCount,
  });
  const emptyTableType = emptyTableTypeProp ?? computedEmptyTableType;

  if (emptyTableType) {
    return (
      <>
        <EmptyConnectionsTable
          tableType={emptyTableType}
          onAddConnection={onAddConnection}
          onAddManagedConnection={onAddManagedConnection}
        />
        <ConnectionModals
          currentModal={currentConnectionModal}
          setCurrentModal={setCurrentConnectionModal}
          setCurrentConnection={setCurrentConnection}
          currentConnections={currentConnection ? [currentConnection] : []}
          managedConnectionSource={managedConnectionSource}
          onConnectionError={onConnectionError}
        />
      </>
    );
  }

  return (
    <>
      <Table
        allowSortByTitle={allowsSorting}
        ariaLabel={ariaLabel}
        bulkActionsAssetLabel={tAssetTypes("plural.connection")}
        bulkSelectActions={bulkSelectActions}
        columns={columns}
        minWidth={TABLE_MIN_WIDTH}
        isLoading={isLoading}
        onSortChange={onSortChange}
        selectableEntities={selectableEntities}
        showCheckboxes={showCheckboxes}
        showOwner={false}
        sortDescriptor={sortDescriptor}
        showFavoriteButton={false}
        titleColumnWidth={TITLE_COLUMN_WIDTH}
      >
        {connections.map((connection) => (
          <ConnectionRow
            allowSelection={showCheckboxes}
            connection={connection}
            isAppNonCompliant={isAppNonCompliant}
            isNonCompliantFilter={isNonCompliantFilter}
            key={connection.id}
            setCurrentConnection={setCurrentConnection}
            setCurrentModal={setCurrentConnectionModal}
            connectionIconOverride={connectionIconOverride}
            isDisplayOnly={isLoadingSecondary}
            showFavoriteButton={false}
          />
        ))}
      </Table>
      {withModals && (
        <ConnectionModals
          currentModal={currentConnectionModal}
          setCurrentModal={setCurrentConnectionModal}
          setCurrentConnection={setCurrentConnection}
          currentConnections={currentConnection ? [currentConnection] : []}
          managedConnectionSource={managedConnectionSource}
          onConnectionError={onConnectionError}
        />
      )}
    </>
  );
};
export default ConnectionsTable;
